import React, { useState } from 'react';
import { ChevronRight, Save, Trash2 } from 'lucide-react';
import { useT } from '../../i18n';

export interface DraftsCardProps {
  /** Saved tournament-setup drafts to list; the card renders nothing when empty. */
  drafts: any[];
  /** Opens a draft to start a new tournament based on it. */
  onOpen: (draft: any) => void;
  /** Deletes a saved draft. */
  onDelete: (draft: any) => void;
}

/**
 * Lists saved tournament-setup drafts ("lagrede oppsett") on the home surface,
 * each with a per-type colour accent and an inline delete-confirmation. Extracted
 * verbatim (byte-for-byte body) from the original artifact (legacy/App.jsx). Pure leaf —
 * renders only HTML + lucide icons and local useState.
 */
export function DraftsCard({ drafts, onOpen, onDelete }: DraftsCardProps) {
  const t = useT();
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!drafts?.length) return null;

  const formatSavedAgo = (ts) => {
    if (!ts) return null;
    const diff = Date.now() - ts;
    const min = Math.floor(diff / 60000);
    if (min < 1) return t('home.common.ago.justNow');
    if (min < 60) return t('home.common.ago.minutes', { min: String(min) });
    const hr = Math.floor(min / 60);
    if (hr < 24)
      return t(hr === 1 ? 'home.common.ago.hour' : 'home.common.ago.hours', {
        count: String(hr),
      });
    const d = Math.floor(hr / 24);
    return t(d === 1 ? 'home.common.ago.day' : 'home.common.ago.days', {
      count: String(d),
    });
  };

  // Each setup type has its own visual accent (matches the cards on MiniTurneringer/Opprett)
  const typeColors = {
    padel24: { bg: '#d9f99d', border: '#a3e635', text: '#365314', accent: '#84cc16' },
    americano: { bg: '#fef9c3', border: '#fde047', text: '#713f12', accent: '#facc15' },
    team_americano: { bg: '#ecfccb', border: '#bef264', text: '#365314', accent: '#84cc16' },
    mexicano: { bg: '#fed7aa', border: '#fb923c', text: '#7c2d12', accent: '#f97316' },
    mixicano: { bg: '#fecaca', border: '#fb7185', text: '#9f1239', accent: '#e11d48' },
    team_mexicano: { bg: '#d9f99d', border: '#84cc16', text: '#365314', accent: '#65a30d' },
    mix_americano: { bg: '#ffedd5', border: '#fb923c', text: '#9a3412', accent: '#ea580c' },
  };

  const count = drafts.length;

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className="flex items-center justify-center w-7 h-7 rounded-full"
          style={{ background: '#e0e7ff', border: '1px solid #a5b4fc' }}
        >
          <Save size={12} style={{ color: '#4338ca' }} />
        </div>
        <div>
          <div
            className="font-mono text-[10px] font-bold tracking-wider uppercase"
            style={{ color: '#4338ca' }}
          >
            {count === 1
              ? t('home.drafts.headingOne')
              : t('home.drafts.headingMany', { count: String(count) })}
          </div>
          <div className="text-[11px] text-ink-3">{t('home.drafts.hint')}</div>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-2">
        {drafts.map((d) => {
          const isConfirming = confirmDelete === d.setupId;
          const colors = typeColors[d.type] || typeColors.padel24;

          return (
            <div
              key={d.setupId}
              className="card-elev overflow-hidden"
              style={{ background: `linear-gradient(to right, ${colors.bg}80, white 60%)` }}
            >
              {isConfirming ? (
                <div className="p-4 bg-rose-soft/40 border-l-4 border-rose">
                  <div className="text-sm font-medium text-ink mb-1">
                    {t('home.drafts.deleteTitle', { name: d.name })}
                  </div>
                  <p className="text-xs text-ink-2 mb-3">{t('home.drafts.deleteLead')}</p>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-ink-2 hover:bg-bg-soft transition-colors"
                    >
                      {t('home.common.cancel')}
                    </button>
                    <button
                      onClick={() => {
                        onDelete(d);
                        setConfirmDelete(null);
                      }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90 flex items-center gap-1.5"
                      style={{ background: 'var(--rose)' }}
                    >
                      <Trash2 size={12} /> {t('home.common.delete')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-stretch">
                  <button
                    onClick={() => onOpen(d)}
                    className="flex-1 px-4 py-3 text-left hover:bg-bg-soft/30 transition-colors flex items-center gap-3"
                  >
                    <span
                      className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: colors.accent + '40', color: colors.text }}
                    >
                      {d.type === 'padel24'
                        ? 'P24'
                        : d.type === 'team_americano'
                          ? 'TAM'
                          : d.type === 'team_mexicano'
                            ? 'TMX'
                            : d.type === 'mexicano'
                              ? 'MX'
                              : d.type === 'mixicano'
                                ? 'MMX'
                                : d.type === 'mix_americano'
                                  ? 'MAM'
                                  : d.type === 'club_americano'
                                    ? 'CAM'
                                    : d.type === 'club_team_americano'
                                      ? 'CTA'
                                      : d.type === 'club_mexicano'
                                        ? 'CMX'
                                        : 'AM'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-display text-sm font-semibold truncate"
                        style={{ color: colors.text }}
                      >
                        {d.name}
                      </div>
                      <div className="text-[10px] font-mono text-ink-3 mt-0.5">
                        {d.typeLabel}
                        {d.playerCount > 0 &&
                          t(
                            d.playerCount === 1
                              ? 'home.drafts.playerOne'
                              : 'home.drafts.playerMany',
                            { count: String(d.playerCount) },
                          )}
                        {d.hasGroups && t('home.drafts.groupsReady')}
                        {d.savedAt && ` · ${formatSavedAgo(d.savedAt)}`}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-ink-3 flex-shrink-0" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(d.setupId)}
                    className="px-3 text-ink-3 hover:text-rose hover:bg-rose-soft/30 transition-colors flex items-center"
                    title={t('home.drafts.deleteAria')}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
